import { GlobalDialogType } from 'components/Modals/GlobalDialog'
import { DayOfWeek } from 'components/Modals/types'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { useAuth } from 'context/AuthContext'
import { useEffect, useState } from 'react'
import { createStaffAvailability, expireStaffAvailability, updateStaffAvailability } from 'services/staff'
import { ServiceApiTypeEnum } from 'types/service'
import { StaffAvailability, StaffAvailabilityDto } from 'types/staff'
import { classNames } from 'utils/dom-helpers'
import {
    useFetch,
} from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { normalizeStaffAvailability } from 'utils/normalize-item'
import { toHHMM12, toYmd } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import { AvailabilityApiStatus } from '.'
import { useTenantContext } from '../../../../context/TenantContext'
import CustomSwitch from '../../../appointmentDrawer/CustomSwitch'
import OverrideRowForm from './OverrideRowForm'

export type AvailabilityOverridesProps = {
    setStatus(status: AvailabilityApiStatus): void
    disable: boolean
}

export default function AvailabilityOverrides({
    setStatus,
    disable
}: AvailabilityOverridesProps) {
    const { setGlobalError } = useAppErrorApiContext()
    const { StaffModal, GlobalDialog } = useAuth()
    const { attributes } = StaffModal
    const { translations } = useAppContext()
    const { services, locations } = useTenantContext()

    const [overrides, setOverrides] = useState<StaffAvailability[]>([])
    const appointments = services
        .filter((item) => item?.service_type === ServiceApiTypeEnum.APPOINTMENT)
    const [show_expired_overrides, setShowExpiredOverrides] = useState<boolean>(false)

    const {
        data: data_availability,
        doFetch: getAvailability
    } = useFetch(
        `/v1/staff/availability/specific?staffId=${attributes?.id}`,
        FetchMethods.GET,
        !!attributes?.id
    )

    useEffect(() => {
        if (data_availability && data_availability.length) {
            const today = new Date()
            let fetched_data = data_availability.filter((item: StaffAvailabilityDto) => (!item.expiryDate && new Date(item.effectiveDate as string) > today)
                || new Date(item.expiryDate as string).setHours(today.getHours(), today.getMinutes(), today.getSeconds(), today.getMilliseconds()) >= today.getTime())

            if (show_expired_overrides) {
                fetched_data = data_availability
            }
            setOverrides(fetched_data.sort((a: StaffAvailabilityDto, b: StaffAvailabilityDto) => new Date(a.effectiveDate as string).getTime() - new Date(b.effectiveDate as string).getTime())
                .map(normalizeStaffAvailability))
        } else {
            setOverrides([])
        }
    }, [data_availability, show_expired_overrides])

    const handleAddAvailability = async () => {
        let global_dialog = {
            title: 'Unable to add new override',
        }
        if (locations?.length == 0) {
            GlobalDialog.setAttributes({
                ...global_dialog,
                message: getTranslation('no_location_found', translations),
                type: GlobalDialogType.ALERT,
            })
            return GlobalDialog.open()
        }

        if (services.length == 0) {
            GlobalDialog.setAttributes({
                ...global_dialog,
                message: getTranslation(
                    'no_appointment_service_found',
                    translations
                ),
                type: GlobalDialogType.ALERT,
            })
            return GlobalDialog.open()
        }

        let staff = {
            id: attributes?.id,
        }
        let location = locations?.[0]?.id ? {
            id: locations?.[0]?.id,
        } : null

        let current_date = new Date()

        let expiryDate = new Date(toYmd(current_date))
        expiryDate.setDate(expiryDate.getDate() + 1)

        let availability: StaffAvailabilityDto = {
            staff: staff,
            block: false,
            service_group_id: '',
            serviceIds: appointments.map((item) => item.id),
            location: location,
            effectiveDate: toYmd(current_date),
            expiryDate: expiryDate.toISOString().substring(0, 10),
            fromTime: '08:00:00 AM',
            toTime: '09:00:00 AM',
        }

        setStatus(AvailabilityApiStatus.SAVING)
        let response = await createStaffAvailability(availability)
        if (response.id) {
            setStatus(AvailabilityApiStatus.SAVED_SUCCESS)
            getAvailability()
        } else {
            setStatus(AvailabilityApiStatus.SAVED_FAILURE)
            setGlobalError(response)
        }
    }

    const handleUpdateAvailability = async (
        idx: number,
        staff_availability: StaffAvailability
    ) => {
        let staff = {
            id: attributes?.id,
        }
        let location = staff_availability?.location ? {
            id: staff_availability.location,
        } : null

        let location_space_ids: string[] = []
        if (staff_availability.location_space) {
            location_space_ids = staff_availability.location_space
        }

        let staff_availability_dto: StaffAvailabilityDto = {
            id: staff_availability.id,
            staff: staff,
            block: staff_availability.availability == 'Block',
            service_group_id: '',
            serviceIds: staff_availability.serviceIds,
            location: location,
            effectiveDate: staff_availability.start_date,
            expiryDate: staff_availability.end_date,
            fromTime: staff_availability.all_day ? undefined : toHHMM12(staff_availability.from_time as Date),
            toTime: staff_availability.all_day ? undefined : toHHMM12(staff_availability.to_time as Date),
            allDay: staff_availability.all_day
        }

        if (location_space_ids.length != 0) {
            staff_availability_dto.locationSpaceIds = location_space_ids
        }

        setStatus(AvailabilityApiStatus.SAVING)
        let response = await updateStaffAvailability(staff_availability_dto)
        if (response.id) {
            setStatus(AvailabilityApiStatus.SAVED_SUCCESS)
            getAvailability()
        } else {
            setStatus(AvailabilityApiStatus.SAVED_FAILURE)
            setGlobalError(response)
        }
    }

    const removeHandler = async (
        idx: number,
        staff_availability: StaffAvailability
    ) => {
        setStatus(AvailabilityApiStatus.SAVING)
        const staff_availability_dto = data_availability.filter((i: StaffAvailabilityDto) => i.id == staff_availability.id)[0]
        let response = await expireStaffAvailability(staff_availability_dto, staff_availability.start_date as string)
        if (response.ok) {
            setStatus(AvailabilityApiStatus.SAVED_SUCCESS)
            getAvailability()
        } else {
            setStatus(AvailabilityApiStatus.SAVED_FAILURE)
            setGlobalError(response)
        }
    }

    return (
        <div className="relative flex flex-col w-full">
            <CustomSwitch
                value={show_expired_overrides}
                onChangeValue={(val) => {
                    setShowExpiredOverrides(val)
                }}
                label={getTranslation(
                    'show_past_overrides',
                    translations
                )}
                className={classNames(disable ? "hidden" : "")}
            />
            <div className="my-6 flex-1">
                <div
                    className='justify-start flex-1 flex flex-col gap-3 w-full'
                >
                    <div className="flex flex-row px-1 py-2.5 bg-primary-lighter/20 gap-2">
                        <Translation
                            render_as="span"
                            className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-[12%]"
                            content_key="availability_col_header"
                        />

                        <Translation
                            render_as="span"
                            className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-[12%]"
                            content_key="location_col_header"
                        />

                        <Translation
                            render_as="span"
                            className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-[12%]"
                            content_key="common.availability_location_spaces"
                        />

                        <Translation
                            render_as="span"
                            className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-[12%]"
                            content_key="services_col_header"
                        />

                        <Translation
                            render_as="span"
                            className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-[20%]"
                            content_key="date"
                        />
                        <Translation
                            render_as="span"
                            className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-[32%]"
                            content_key="time_range_col_header"
                        />
                    </div>

                    <div className="flex flex-col gap-4 max-h-large overflow-auto text-sm">
                        {overrides.map((override: any, idx: number) => {
                            return (
                                <div
                                    key={"override_row_" + idx}
                                    id={"override_row_" + idx}
                                >
                                    <OverrideRowForm
                                        override={override}
                                        idx={idx}
                                        onAttributesChanged={(o: any, id: number) => {
                                            handleUpdateAvailability(id, o)
                                        }}
                                        removeHandler={(id: number, o: any) => {
                                            removeHandler(id, o)
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>
                    <button
                        className={classNames("flex justify-center mt-3 border-dashed border-2 border-stone-400 h-10 items-center text-stone-400 rounded",
                            disable ? "cursor-not-allowed" : ""
                        )}
                        onClick={handleAddAvailability}
                        disabled={disable}
                    >
                        <i className="feather-plus text-lg" />
                        <Translation content_key="add_override" />
                    </button>
                </div>
            </div>
        </div>
    )
}
